import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useViewModelReady } from '../../hooks/useViewModel';
import {
  AdminHeader,
  AdminLayout,
  AdminNavSheet,
  ADMIN_TOOLS,
  AdminToolId,
  adminToolPath,
  DEFAULT_ADMIN_TOOL_ID,
  getAdminToolIdFromSlug,
} from '../../components/AdminLayout';
import { useAdminViewModel } from './viewModel';
import { UsersTool } from './tools/UsersTool';
import { CampaignsTool } from './tools/CampaignsTool';
import { TaskTypesTool } from './tools/TaskTypesTool';
import { LlmTestTool } from './tools/LlmTestTool';
import { TokenDashboardTool } from './tools/TokenDashboardTool';
import { AiDetectionDashboardTool } from './tools/AiDetectionDashboardTool';
import { GoogleNexusTool } from './tools/GoogleNexusTool';
import { HarborRunnersTool } from './tools/HarborRunnersTool';
import { paths } from '../../routes/paths';

// Re-exports kept so BrowseTasksPage and any other admin sub-pages can import
// these from `pages/AdminPage` without rippling the move into AdminLayout.
export { AdminHeader, AdminNavSheet, ADMIN_TOOLS };
export type { AdminToolId };

export function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const vm = useAdminViewModel();
  useViewModelReady(vm.initialize);

  // Canonicalise the URL. Three cases land here:
  //   1) `/admin` with no tool slug — redirect to the default tool.
  //   2) `/admin` (or `/admin/<slug>`) with a legacy `?tool=<id>` query —
  //      strip the query and redirect to the matching path.
  //   3) `/admin/<unknown-slug>` — fall back to the default tool.
  useEffect(() => {
    const legacy = new URLSearchParams(location.search).get('tool');
    if (legacy && isAdminToolId(legacy)) {
      navigate(adminToolPath(legacy), { replace: true });
      return;
    }
    if (vm.routeToolSlug === null) {
      navigate(adminToolPath(DEFAULT_ADMIN_TOOL_ID), { replace: true });
      return;
    }
    if (vm.unknownSlug) {
      navigate(adminToolPath(DEFAULT_ADMIN_TOOL_ID), { replace: true });
    }
  }, [location.search, navigate, vm.routeToolSlug, vm.unknownSlug]);

  if (!isAdmin) {
    return <Navigate to={paths.queue()} replace />;
  }

  return (
    <AdminLayout
      activeId={vm.activeTool}
      onSelectTool={(id) => vm.setActiveTool(id)}
      backLabel="Back to queue"
      onBack={() => navigate(paths.queue())}
    >
      {({ mobileNavTrigger }) =>
        vm.busy ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <ToolContent toolId={vm.activeTool} mobileNavTrigger={mobileNavTrigger} />
        )
      }
    </AdminLayout>
  );
}

function isAdminToolId(value: string | null): value is AdminToolId {
  if (!value) return false;
  if (ADMIN_TOOLS.some((tool) => tool.id === value)) return true;
  return getAdminToolIdFromSlug(value) !== null;
}

function ToolContent({
  toolId,
  mobileNavTrigger,
}: {
  toolId: AdminToolId;
  mobileNavTrigger?: React.ReactNode;
}) {
  if (toolId === 'users') return <UsersTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'campaigns') return <CampaignsTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'taskTypes') return <TaskTypesTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'googleNexus') return <GoogleNexusTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'llmTest') return <LlmTestTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'tokenDashboard') return <TokenDashboardTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'aiDetectionDashboard') return <AiDetectionDashboardTool mobileNavTrigger={mobileNavTrigger} />;
  if (toolId === 'harborRunners') return <HarborRunnersTool mobileNavTrigger={mobileNavTrigger} />;
  return null;
}
